import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { markPaid, getPaidSession } from "../lib/paidDownloads";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PaymentSuccess — the page Stripe redirects to after a successful checkout.
 * It:
 *   1. Reads session_id + slug from the query string.
 *   2. Polls GET /api/payments/status/{session_id} every 2s (up to ~30s).
 *   3. Once payment_status === "paid", writes the paid slug to localStorage
 *      and auto-triggers the PDF download.
 *   4. Offers a re-download button and a "back to recipe" link.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const slug = params.get("slug");

  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [attempts, setAttempts] = useState(0);
  const downloadTriggered = useRef(false);

  useEffect(() => {
    if (!sessionId || !slug) {
      setState("error");
      return;
    }
    // Fast path — the browser may already have this session marked paid
    // (double-tab, refresh, etc). Skip straight to the download.
    if (getPaidSession(slug) === sessionId) {
      setState("paid");
      return;
    }

    const MAX = 15; // ~30s total
    let cancelled = false;
    let n = 0;

    const tick = async () => {
      if (cancelled) return;
      n += 1;
      setAttempts(n);
      try {
        const r = await api.paymentStatus(sessionId);
        if (r.payment_status === "paid") {
          markPaid(slug, sessionId);
          setState("paid");
          return;
        }
      } catch {
        /* keep polling on transient errors */
      }
      if (n >= MAX) {
        setState("timeout");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();

    return () => { cancelled = true; };
  }, [sessionId, slug]);

  // Auto-fire the download the moment we flip to "paid".
  useEffect(() => {
    if (state === "paid" && sessionId && slug && !downloadTriggered.current) {
      downloadTriggered.current = true;
      const url = `${BACKEND_URL}/api/glossary/${slug}/download?session_id=${encodeURIComponent(sessionId)}`;
      // A hidden iframe navigation is the most reliable way to trigger a
      // file download without leaving the current page.
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 60000);
    }
  }, [state, sessionId, slug]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 md:py-28 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 size={40} className="animate-spin mx-auto mb-6" style={{ color: "var(--olive)" }} />
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">
            Confirming your payment
          </div>
          <h1 className="font-display italic text-4xl md:text-5xl leading-tight mb-4">
            Just a moment…
          </h1>
          <p className="font-body opacity-70">
            Stripe is telling us your card cleared. This usually takes 2–5 seconds. (Attempt {attempts})
          </p>
        </>
      )}

      {state === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto mb-6" style={{ color: "#087A46" }} />
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">
            Thank you — payment received
          </div>
          <h1 className="font-display italic text-4xl md:text-5xl leading-tight mb-4">
            Your recipe is downloading.
          </h1>
          <p className="font-body opacity-70 mb-8">
            If the download didn't start, click the button below. This browser will
            remember your purchase, so you can re-download this recipe any time.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`${BACKEND_URL}/api/glossary/${slug}/download?session_id=${encodeURIComponent(sessionId || "")}`}
              className="btn-primary btn-accent"
              data-testid="payment-success-download-again"
            >
              <Download size={16} /> Download again
            </a>
            <Link to={slug ? `/recipe/${slug}` : "/recipes"} className="btn-primary btn-outline" data-testid="payment-success-back">
              Back to the recipe
            </Link>
          </div>
        </>
      )}

      {state === "timeout" && (
        <>
          <Loader2 size={40} className="mx-auto mb-6 opacity-50" />
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">
            Still processing
          </div>
          <h1 className="font-display italic text-4xl md:text-5xl leading-tight mb-4">
            Your payment is taking a little longer than usual.
          </h1>
          <p className="font-body opacity-70 mb-8">
            Refresh this page in a minute — the download will unlock automatically
            once Stripe confirms your card. If it doesn't, get in touch and I'll
            sort it out personally.
          </p>
          <button onClick={() => window.location.reload()} className="btn-primary" data-testid="payment-success-retry">
            Refresh
          </button>
        </>
      )}

      {state === "error" && (
        <>
          <XCircle size={48} className="mx-auto mb-6" style={{ color: "var(--tomato)" }} />
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">
            Something's missing
          </div>
          <h1 className="font-display italic text-4xl md:text-5xl leading-tight mb-4">
            We couldn't find your payment details.
          </h1>
          <p className="font-body opacity-70 mb-8">
            Head back to the recipe and try the download button again.
          </p>
          <Link to="/recipes" className="btn-primary btn-accent" data-testid="payment-success-recover">
            Back to the recipes
          </Link>
        </>
      )}
    </div>
  );
}
